import {Action, Attributes, Card} from 'platform/components';
import {Heading, Hide, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetFileByIdQuery} from '@dms/api/base';
import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds, GetVehicleApiResponseV2} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {EntityCustomFields, usePermissions} from '@dms/shared';
import {getVehicleMakeModels, AppDispatch} from '@dms/teas';

import {composePath, useNavigate} from 'shared/web';

import {useVehicleDetailsParameters} from '../../../hooks/useVehicleDetailsParameters';

interface CarDetailsDetailsProps {
  vehicle: GetVehicleApiResponseV2;
  vehicleId: string;
  isSaleVehicle: boolean;
}

export function VehicleDetail(props: CarDetailsDetailsProps) {
  const navigate = useNavigate();

  const {data: vehicleParticipation} = useGetParticipationQuery({
    recordId: props.vehicle.id,
    resourceId: EntityResourceIds.vehicle,
  });

  const dispatch = useDispatch<AppDispatch>();

  useEffect(
    function loadVehicleMakeModels() {
      if (props.vehicle.type && props.vehicle.make) {
        dispatch(
          getVehicleMakeModels.action({
            vehicleType: props.vehicle.type,
            make: props.vehicle.make,
          })
        );
      }
    },
    [props.vehicle.type, props.vehicle.make] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  // Fetch battery certificate file metadata if certificateFileId exists
  const {data: certificateFile} = useGetFileByIdQuery(
    {fileId: (props.vehicle as {certificateFileId?: string | null})?.certificateFileId ?? ''},
    {skip: isNilOrEmpty((props.vehicle as {certificateFileId?: string | null})?.certificateFileId)}
  );

  const [
    modelSpecifications,
    engineSpecifications,
    consumptionAndEmissionSpecifications,
    metricsSpecifications,
  ] = useVehicleDetailsParameters(props.vehicle, certificateFile);

  const [canUpdateVehicle, canSetCustomVehicleId, canEditCustomFields] = usePermissions({
    permissionKeys: ['updateVehicle', 'setCustomIdVehicle', 'vehicleCustomFields'],
    scopes: {
      updateVehicle: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      setCustomIdVehicle: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });
  const canEditVehicle = canUpdateVehicle && canSetCustomVehicleId;

  const navigateToEditVehicle = () =>
    navigate(
      composePath(vehiclesRoutes.editV2Tab, {
        params: {id: props.vehicle.id, tab: 'basic-information'},
      })
    );

  const isTrailerOrSemiTrailer =
    props.vehicle.type === 'VEHICLETYPE_TRAILER' ||
    props.vehicle.type === 'VEHICLETYPE_SEMI_TRAILER';

  const editAction: Action = {
    variant: 'link',
    type: 'button',
    leftIcon: 'image/edit',
    onClick: navigateToEditVehicle,
    title: i18n.t('general.actions.edit'),
  };

  return (
    <VStack spacing={2}>
      <Card
        title={i18n.t('entity.vehicle.labels.modelSpecification')}
        actions={
          canEditVehicle
            ? [
                {
                  ...editAction,
                  'data-testid': testIds.vehicles.detail('vehicle-edit-model-specifications'),
                },
              ]
            : undefined
        }
        data-testid={testIds.vehicles.detail('generalInformation')}
      >
        <Attributes
          rows={modelSpecifications}
          data-testid={testIds.vehicles.detail('vehicle-model-specifications')}
        />
      </Card>

      <Card
        title={i18n.t('entity.vehicle.labels.additionalInformation')}
        actions={
          canEditVehicle
            ? [
                {
                  ...editAction,
                  'data-testid': testIds.vehicles.detail('vehicle-edit-additional-info'),
                },
              ]
            : undefined
        }
        data-testid={testIds.vehicles.detail('additionalInformation')}
      >
        <VStack spacing={1}>
          <Hide when={isTrailerOrSemiTrailer}>
            <Heading size={4}>{i18n.t('entity.vehicle.labels.engine')}</Heading>
            <Attributes
              rows={engineSpecifications}
              data-testid={testIds.vehicles.detail('vehicle-model-engine')}
            />
            <Heading size={4}>
              {i18n.t('entity.vehicle.labels.consumptionAndEmissionSectionTitle')}
            </Heading>
            <Attributes
              rows={consumptionAndEmissionSpecifications}
              data-testid={testIds.vehicles.detail(
                'vehicle-model-consumptionAndEmissionSectionTitle'
              )}
            />
          </Hide>

          <Heading size={4}>{i18n.t('entity.vehicle.labels.dimensions')}</Heading>
          <Attributes
            rows={metricsSpecifications}
            data-testid={testIds.vehicles.detail('vehicle-model-dimensions')}
          />
        </VStack>
      </Card>

      <EntityCustomFields
        recordId={props.vehicle.id}
        resourceId={EntityResourceIds.vehicle}
        isEditable={canEditCustomFields}
        data-testid={testIds.vehicles.detail('vehicle-custom-fields')}
      />
    </VStack>
  );
}
